import {
  dehydrate,
  QueryClient,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from 'react-query'
import { Api } from 'types/Api'
import { FetchOptions } from 'utils/createApiClient'
import { contentfulClient } from 'services/ApiClient'
import { BlogPostItemFields } from 'features/Blog/screens/Post'
import { BlogPostMetadata } from 'features/Blog/screens/Post/Post'
import { FetchBlogTags } from './useFetchBlogTags'

export namespace FetchBlogPosts {
  export type Response = {
    items: {
      fields: BlogPostItemFields
      metadata: BlogPostMetadata
      sys: {
        id: string
      }
    }[]
    limit: number
    skip: number
    total: number
  }
  export type Variables = string[]
  export type Error = Api.Error
  export type Options = UseInfiniteQueryOptions<Response, Error>
}

const createKey = (tags: FetchBlogPosts.Variables) => ['posts', { tags }]

const queryFetcher =
  (tags: FetchBlogPosts.Variables) =>
  ({ pageParam = 0 }) => {
    const query: {
      content_type: string
      'metadata.tags.sys.id[in]'?: string
      limit: number
      skip: number
    } = {
      content_type: 'blogPost',
      limit: 9,
      skip: pageParam || 0,
    }

    if (tags.length > 0) {
      query['metadata.tags.sys.id[in]'] = tags.join(',')
    }

    return contentfulClient.getEntries<BlogPostItemFields>(query)
  }

export const useFetchBlogPosts = (
  tags: FetchBlogPosts.Variables,
  options?: FetchBlogPosts.Options
) => {
  return useInfiniteQuery<FetchBlogPosts.Response, FetchBlogPosts.Error>(
    createKey(tags),
    queryFetcher(tags),
    {
      ...options,
      getNextPageParam: (lastPage, pages) =>
        lastPage.total > lastPage.limit * pages.length ? lastPage.limit * pages.length : null,
    }
  )
}

export const prefetchBlogPosts = async () => {
  const queryClient = new QueryClient()

  await queryClient.prefetchQuery<FetchBlogTags.Response, FetchBlogTags.Error>(['tags'], () => {
    return contentfulClient.getTags()
  })

  await queryClient.prefetchInfiniteQuery<FetchBlogPosts.Response, FetchBlogPosts.Error>(
    createKey([]),
    queryFetcher([])
  )

  return {
    dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
  }
}
